import { Divider } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import convertSize from "convert-size";
import { noop } from "lodash";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { DangerButton, PrimaryButton } from "../../../../components/button";
import { useFileLoader } from "../../../../components/hooks/file-loader";
import { Icon } from "../../../../components/icons";
import { Flex } from "../../../../components/layout";
import { StepperContext } from "../../../../components/stepper/context";
import { CryptoDTO } from "../../../../store/crypto/models/dto";
import { CryptoService } from "../../../../store/crypto/service";
import { NotificationActions } from "../../../../store/notifications/actions";
import { BlockchainToolsRoutes } from "../../routes";
import { Container, StyledWidget } from "./components";

const FileInput = styled.input.attrs({ type: "file" })`
  display: none;
`;

const FileLabel = styled.label`
  display: block;
  cursor: pointer;
`;

const UploadCTA = styled(PrimaryButton).attrs({ variant: "outlined" })`
  pointer-events: none;
  margin: 1em 2em 1em 0;
  width: 8em;
`;

const Table = styled.table`
  width: 100%;
`;

const Cell = styled.td`
  padding: 0.5em 1em;
`;

type FraudReport = CryptoDTO.Responses.GetSingleFraudReport.T;

const useSubmitDocs = () => {
  const fileLoader = useFileLoader();
  return useCallback(
    (data: CryptoDTO.Requests.UploadDocument.T) =>
      fileLoader(CryptoService.uploadDocumentToFraudReport(data)).catch(noop),
    []
  );
};

const SingleDocument: FunctionComponent<
  Pick<FileList[0], "name" | "size"> & { onRemove: () => void }
> = ({ name, size, onRemove }) => {
  return (
    <tr>
      <Cell>{name}</Cell>
      <Cell>{convertSize(size)}</Cell>
      <Cell>
        <DangerButton variant="text" onClick={onRemove} disabled>
          <Icon type="trash" />
        </DangerButton>
      </Cell>
    </tr>
  );
};

export const UploadDocs: React.FunctionComponent<{
  reportId?: FraudReport["id"];
}> = ({ reportId }) => {
  const [files, setFiles] = useState<FileList | null>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const onSubmit = useSubmitDocs();
  const { setValidation } = useContext(StepperContext);

  useEffect(() => {
    if (reportId === undefined) {
      router.stateService.go(BlockchainToolsRoutes.names.reportAll);
    }
  }, [reportId, router.stateService]);

  const { t } = useTranslation("blockchain-tools");

  useEffect(() => {
    setValidation(() => () => {
      if (reportId === undefined) return Promise.reject();
      if (files === null || files.length < 1) {
        dispatch(
          NotificationActions.setNotification({
            title: "you-must-choose-at-least-one-document-to-upload",
            variant: "error",
          })
        );
        return Promise.reject();
      }
      return onSubmit({
        reportId: reportId,
        files,
      })
        .then(() =>
          router.stateService.go(BlockchainToolsRoutes.names.reportAll)
        )
        .then(noop);
    });
    return () => setValidation(() => () => Promise.resolve());
  }, [files, router, reportId, t]);

  return (
    <Container>
      <StyledWidget
        title={t("upload-any-supported-docs-or-images-or-screenshots")}
      >
        <Flex>
          <FileInput
            multiple
            onChange={(e) => {
              setFiles(e.target.files);
            }}
            id="evidenceInput"
          />
          <FileLabel htmlFor="evidenceInput">
            <UploadCTA>{t("upload", { ns: "common" })}</UploadCTA>
          </FileLabel>
          <p>
            {t("jpg-png-pdf-doc-xls-supported-file-types", { ns: "fields" })}
          </p>
        </Flex>
        <Divider light />
        {files ? (
          <Table>
            <tbody>
              {Array.from(files).map((file) => (
                <SingleDocument
                  key={file.name}
                  name={file.name}
                  size={file.size}
                  onRemove={() => {}}
                />
              ))}
            </tbody>
          </Table>
        ) : null}
      </StyledWidget>
    </Container>
  );
};
